import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export type UserStatus = 'pending' | 'active' | 'inactive';

export interface UserLookupItem {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  isSuperAdmin: boolean;
  status: UserStatus;
  isActive: boolean;
  lastLoginAt: string | null;
  hasSignedIn: boolean;
}

interface UserLookupResponse {
  users: UserLookupItem[];
  total: number;
}

/**
 * Lightweight user list for picker components — any authenticated user can call.
 * Server returns active users by default, sorted by name.
 */
export function useUserLookup(opts?: { q?: string; includeInactive?: boolean }) {
  return useQuery({
    queryKey: ['users', 'lookup', opts?.q || '', opts?.includeInactive ? 'all' : 'active'],
    queryFn: () =>
      api.get<UserLookupResponse>('/users/lookup', {
        ...(opts?.q ? { q: opts.q } : {}),
        ...(opts?.includeInactive ? { active: 'all' } : {}),
      }),
    staleTime: 60 * 1000,
  });
}

/**
 * Admin-only — pre-provision a stub user before they have signed in.
 * When they later sign in via Google with the same email, the row is reused.
 * Pre-provisioned users go straight to status='active' (the admin is implicitly
 * approving them by creating the row), so you may optionally assign groups here.
 */
export function useCreateStubUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      email: string;
      name: string;
      groupIds?: string[];
      isSuperAdmin?: boolean;
    }) => api.post<{ user: UserLookupItem }>('/admin/users', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}
